import React from "react";
import type { LucideIcon } from "lucide-react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { SECTION_HEADER_CLASS } from "./section-header";
import { Hint } from "./icon-button";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { cn } from "@/lib/utils";

/** Inside a section body every section is a subsection (П1б) unless told otherwise. */
const NestedContext = React.createContext(false);

/** «Свернуть все секции» / «Свернуть все»: every section (or tree, by its own listener) inside `root` folds. */
export const COLLAPSE_EVENT = "roll1:collapse-sections";
export function collapseSectionsIn(root: Element | null, scope: "sections" | "tree" = "sections"): void {
  if (root === null) return;
  window.dispatchEvent(new CustomEvent(COLLAPSE_EVENT, { detail: { root, scope } }));
}

interface SidebarPanelProps {
  /** Unique key for persisting collapse state */
  id: string;
  title: string;
  icon?: LucideIcon;
  /** Allow collapsing (default: true) */
  collapsible?: boolean;
  /** Default collapsed state (default: false) */
  defaultCollapsed?: boolean;
  /** Right-side header actions: ≤2 IconButtons (a RowMenu «⋯» and an ⓘ count) — П1. */
  actions?: React.ReactNode;
  /** The count after the title (`text-meta` dim); `countTone="warn"` — the warn pill of «Замечания». */
  count?: React.ReactNode;
  countTone?: "plain" | "warn";
  countTestid?: string;
  /** ⓘ after the title: the words that used to be a plate under the header (П7). */
  info?: string;
  /** `section` (П1: caps, chevron 12, 32 px, sticky) or `sub` (П1б: sentence case 12/600, chevron 10, 28 px, static). */
  level?: "section" | "sub";
  /** The title stays for screen readers but is not drawn (a tab above already says it). */
  hideTitle?: boolean;
  /** Fill available height (for chat-like panels) */
  flex?: boolean;
  /** `tight` — a list: no gap, no side padding (rows carry their own 6 px). */
  tight?: boolean;
  bodyClassName?: string;
  /** testid on the <section>. */
  testid?: string;
  /** The region name (getByRole("region")); default = the title. */
  regionLabel?: string;
  /** Collapsed state driven from outside (a search opens every group). */
  forceOpen?: boolean;
  children: React.ReactNode;
}

/**
 * ONE section header for every section of the table sidebar and the Подготовка rail (brief П1 / П1б):
 * chevron left (▸/▾), the title, the count, ≤2 icons on the right; a click anywhere on the header folds it and the state
 * is remembered (localStorage `panel-<id>-collapsed`). A section header sticks under the panel header (`--phead-h`).
 */
const SidebarPanel = React.memo(function SidebarPanel({
  id,
  title,
  // `icon` stays in the API (callers pass it) but is not drawn: headers are one style.
  icon: _icon,
  collapsible = true,
  defaultCollapsed = false,
  actions,
  count,
  countTone = "plain",
  countTestid,
  info,
  level: levelProp,
  hideTitle,
  flex,
  tight,
  bodyClassName,
  testid,
  forceOpen,
  regionLabel,
  children,
}: SidebarPanelProps) {
  const nested = React.useContext(NestedContext);
  const level = levelProp ?? (nested ? "sub" : "section");
  const [collapsed, setCollapsed] = useLocalStorage(`panel-${id}-collapsed`, defaultCollapsed);
  const ref = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (!collapsible) return undefined;
    const onCollapse = (e: Event): void => {
      const detail = (e as CustomEvent<{ root: Element; scope: string }>).detail;
      if (detail.scope === "sections" && ref.current !== null && detail.root.contains(ref.current) && level === "section") setCollapsed(true);
    };
    window.addEventListener(COLLAPSE_EVENT, onCollapse);
    return () => window.removeEventListener(COLLAPSE_EVENT, onCollapse);
  }, [collapsible, level, setCollapsed]);

  const isCollapsed = collapsible && collapsed && !forceOpen;
  const sub = level === "sub";
  const toggle = (): void => setCollapsed(!isCollapsed);
  const Chevron = isCollapsed ? ChevronRight : ChevronDown;

  return (
    <section
      ref={ref}
      className="sidebar-panel"
      data-component={`SidebarPanel-${id}`}
      data-level={level}
      data-flex={flex || undefined}
      data-testid={testid}
      aria-label={regionLabel ?? title}
    >
      <div
        className="sidebar-panel__header"
        data-collapsed={isCollapsed || undefined}
        onClick={collapsible ? toggle : undefined}
        style={collapsible ? { cursor: "pointer" } : undefined}
      >
        <h3 className={cn("sidebar-panel__title", hideTitle && "sr-only")}>
          {collapsible ? (
            // The accordion pattern: the heading holds the button (keyboard + screen readers); the whole header row clicks too.
            <button
              type="button"
              className="sidebar-panel__toggle"
              aria-expanded={!isCollapsed}
              title={title}
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
            >
              <span className="sidebar-panel__chevron" aria-hidden="true">
                <Chevron size={sub ? 10 : 12} />
              </span>
              <span className={cn(sub ? "sidebar-panel__subname" : SECTION_HEADER_CLASS, "min-w-0 truncate")}>{title}</span>
            </button>
          ) : (
            <span className="sidebar-panel__toggle" title={title}>
              <span className="sidebar-panel__chevron" aria-hidden="true" />
              <span className={cn(sub ? "sidebar-panel__subname" : SECTION_HEADER_CLASS, "min-w-0 truncate")}>{title}</span>
            </span>
          )}
        </h3>
        {count !== undefined && count !== null && count !== "" ? (
          <span
            className={countTone === "warn" ? "sidebar-panel__count sidebar-panel__count--warn" : "sidebar-panel__count"}
            data-testid={countTestid}
          >
            {count}
          </span>
        ) : null}
        {info ? (
          <Hint text={info}>
            <span
              className="sidebar-panel__info"
              tabIndex={0}
              role="img"
              aria-label={info}
              onClick={(e) => e.stopPropagation()}
            >
              <Info size={12} aria-hidden="true" />
            </span>
          </Hint>
        ) : null}
        <span className="flex-1" />
        {actions ? (
          <div className="sidebar-panel__actions" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        ) : null}
      </div>
      {!isCollapsed && (
        <div className={cn("sidebar-panel__body", tight && "sidebar-panel__body--tight", bodyClassName)}>
          <NestedContext.Provider value={true}>{children}</NestedContext.Provider>
        </div>
      )}
    </section>
  );
});

export default SidebarPanel;
